import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Clock, MapPin, Bookmark, Loader2, AlertTriangle, CalendarPlus, BellRing } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import { notify } from '../utils/notifications';
import { useApp } from '../context/AppContext';
import { useEventData } from '../context/EventDataContext';
import { useFavorites } from '../hooks/useFavorites';
import { conflictingIds, sessionTypeLabel } from '../utils/schedule';
import { scheduleSessionReminders, isLocalNotificationsSupported } from '../native/localNotifications';
import { exportFile, slugify } from '../utils/exportShare';
import { buildICS } from '../utils/exporters';
import { resolveAgendaFilters, type Session, type SessionType, type ConferenceEvent } from '@conference/shared';

// ─── Track styles ─────────────────────────────────────────────────────────────

const TRACK_STYLES: Record<string, { bg: string; text: string }> = {
  keynote:    { bg: 'rgba(14,165,233,0.15)',  text: '#38BDF8' },
  talk:       { bg: 'rgba(124,58,237,0.15)',  text: '#A78BFA' },
  workshop:   { bg: 'rgba(5,150,105,0.15)',   text: '#34D399' },
  panel:      { bg: 'rgba(217,119,6,0.15)',   text: '#FBBF24' },
  poster:     { bg: 'rgba(239,68,68,0.15)',   text: '#F87171' },
  networking: { bg: 'rgba(236,72,153,0.15)',  text: '#F472B6' },
  break:      { bg: 'rgba(100,116,139,0.15)', text: '#94A3B8' },
};

function trackStyle(type: SessionType | string): { bg: string; text: string } {
  return TRACK_STYLES[type?.toLowerCase()] ?? { bg: 'rgba(100,116,139,0.15)', text: '#94A3B8' };
}

function formatTime(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function formatDay(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

type AgendaSession = Session & {
  papers?: { presentation_topic?: string; presentation_start_time?: string; presentation_end_time?: string }[];
  sourceSessionId?: string;
  sourceSessionTitle?: string;
  topic?: string;
};

export default function AgendaScreen() {
  const navigate = useNavigate();
  const { currentEventId, selectedEvent } = useApp();
  const { services: { eventService, sessionService }, load } = useEventData();
  const { toggleFavorite, isFavorite } = useFavorites();
  const agendaFilterOverrides = selectedEvent?.id === currentEventId ? selectedEvent.app_modules?.agenda?.filters ?? undefined : undefined;

  const [sessions, setSessions] = useState<Session[]>([]);
  const [event, setEvent] = useState<ConferenceEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeDayIdx, setActiveDayIdx] = useState(0);
  const [activeTrack, setActiveTrack] = useState('all');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (currentEventId) loadSessions(currentEventId);
  }, [currentEventId]);

  async function loadSessions(eventId: string) {
    setLoading(true);
    let servedStale = false;
    try {
      const [data, ev] = await Promise.all([
        load(
          `sessions:${eventId}`,
          () => sessionService.getAll(eventId),
          { onStale: () => { servedStale = true; } },
        ),
        load(`event:${eventId}`, () => eventService.getById(eventId)).catch(() => null),
      ]);
      setSessions(data);
      setEvent(ev);
      if (servedStale) notify.info('Offline — showing your saved agenda');
    } catch (err) {
      console.error('Failed to load sessions', err);
      notify.error('Could not load sessions');
    } finally {
      setLoading(false);
    }
  }

  // Bookmarked sessions across all days, and which of them clash in time.
  const savedSessions = useMemo(
    () => sessions.filter((s) => isFavorite(s.id, 'session')),
    [sessions, isFavorite],
  );
  const conflictSet = useMemo(() => conflictingIds(savedSessions), [savedSessions]);

  const handleAddToCalendar = async () => {
    if (savedSessions.length === 0) { notify.info('Bookmark sessions first'); return; }
    setBusy(true);
    try {
      await exportFile({
        filename: `${event?.name ? slugify(event.name) : 'agenda'}.ics`,
        content: buildICS(savedSessions, event),
        mimeType: 'text/calendar',
        dialogTitle: 'Add to calendar',
      });
      notify.success(`${savedSessions.length} session${savedSessions.length === 1 ? '' : 's'} → calendar`);
    } catch {
      notify.error('Could not build calendar');
    } finally {
      setBusy(false);
    }
  };

  const handleEnableReminders = async () => {
    if (savedSessions.length === 0) { notify.info('Bookmark sessions first'); return; }
    if (!isLocalNotificationsSupported()) {
      notify.info('Reminders work in the installed app');
      return;
    }
    setBusy(true);
    try {
      const { scheduled } = await scheduleSessionReminders(savedSessions);
      if (scheduled > 0) notify.success(`Reminders set for ${scheduled} upcoming session${scheduled === 1 ? '' : 's'}`);
      else notify.info('No upcoming sessions to remind you about');
    } catch {
      notify.error('Could not set reminders');
    } finally {
      setBusy(false);
    }
  };

  // Derive unique sorted days from sessions
  const days = useMemo(() => {
    const unique = [...new Set(sessions.map((s) => s.date))].sort();
    return unique;
  }, [sessions]);

  const activeDay = days[activeDayIdx];

  const agendaFilters = useMemo(() => resolveAgendaFilters(
    sessions.map(({ type }) => type),
    selectedEvent?.id === currentEventId ? selectedEvent.app_modules : undefined,
  ), [currentEventId, selectedEvent, sessions]);

  useEffect(() => {
    if (activeTrack !== 'all' && !agendaFilters.some(({ type }) => type === activeTrack)) {
      setActiveTrack('all');
    }
  }, [activeTrack, agendaFilters]);

  const filtered = useMemo(() => {
    return sessions.filter((s) => {
      const matchDay = s.date === activeDay;
      const matchTrack = activeTrack === 'all' || s.type.toLowerCase() === activeTrack;
      return matchDay && matchTrack;
    });
  }, [sessions, activeDay, activeTrack]);

  const agendaItems = useMemo((): AgendaSession[] => filtered.flatMap((session) => {
    const papers = (session as AgendaSession).papers;
    if (session.type !== 'talk' || !papers?.some((paper) => paper.presentation_topic)) return session;
    const byTopic = new Map<string, typeof papers>();
    papers.forEach((paper) => {
      const topic = paper.presentation_topic;
      if (!topic) return;
      byTopic.set(topic, [...(byTopic.get(topic) ?? []), paper]);
    });
    return [...byTopic].map(([topic, topicPapers]) => ({
      ...session,
      id: `${session.id}:${topic}`,
      title: topic,
      start_time: topicPapers[0].presentation_start_time ?? session.start_time,
      end_time: topicPapers.at(-1)?.presentation_end_time ?? session.end_time,
      sourceSessionId: session.id,
      sourceSessionTitle: session.title,
      topic,
    }));
  }).sort((left, right) => (
    left.date.localeCompare(right.date)
    || left.start_time.localeCompare(right.start_time)
    || left.title.localeCompare(right.title)
  )), [filtered]);

  const toggleSave = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const fav = isFavorite(id, 'session');
    toggleFavorite(id, 'session');
    if (!fav) notify.custom.sessionSaved();
    else notify.info('Removed from schedule');
  };

  return (
    <div
      className="size-full flex flex-col overflow-hidden transition-colors duration-300"
      style={{ backgroundColor: 'var(--app-bg)' }}
    >
      {/* Header */}
      <div
        className="app-screen-header px-4 pt-12 pb-3 border-b shrink-0"
        style={{ borderColor: 'var(--app-divider)', backgroundColor: 'var(--app-bg)' }}
      >
        <div className="flex items-center justify-between">
          <h1 className="text-[28px] tracking-tight" style={{ color: 'var(--app-text)' }}>Agenda</h1>
          {savedSessions.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleEnableReminders}
                disabled={busy}
                aria-label="Enable reminders"
                className="size-9 rounded-full flex items-center justify-center border active:scale-90 transition-transform disabled:opacity-50"
                style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
              >
                <BellRing className="w-4 h-4" style={{ color: 'var(--app-text-subtle)' }} />
              </button>
              <button
                onClick={handleAddToCalendar}
                disabled={busy}
                aria-label="Add saved sessions to calendar"
                className="flex items-center gap-1.5 px-3 h-9 rounded-full border active:scale-95 transition-transform disabled:opacity-50"
                style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--app-text-subtle)' }} />
                      : <CalendarPlus className="w-4 h-4" style={{ color: 'var(--app-text-subtle)' }} />}
                <span className="text-xs" style={{ color: 'var(--app-text-subtle)' }}>Calendar</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Day tabs */}
      {!loading && days.length > 0 && (
        <div
          className="flex gap-2 px-4 py-3 overflow-x-auto shrink-0 border-b"
          style={{ borderColor: 'var(--app-divider)', scrollbarWidth: 'none' }}
        >
          {days.map((day, i) => (
            <button
              key={day}
              onClick={() => setActiveDayIdx(i)}
              className="flex-none px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors"
              style={
                activeDayIdx === i
                  ? { background: 'var(--app-accent-solid)', color: '#fff' }
                  : { background: 'var(--app-surface)', color: 'var(--app-text-subtle)' }
              }
            >
              {formatDay(day)}
            </button>
          ))}
        </div>
      )}

      {/* Track filter pills */}
      <div
        className="flex gap-2 px-4 py-2.5 overflow-x-auto shrink-0 border-b"
        style={{ borderColor: 'var(--app-divider)', scrollbarWidth: 'none' }}
      >
        {[{ type: 'all', label: 'All' }, ...agendaFilters].map((track) => (
          <button
            key={track.type}
            onClick={() => setActiveTrack(track.type)}
            className="flex-none px-3 py-1.5 rounded-full text-xs transition-colors"
            style={
              activeTrack === track.type
                ? { background: 'var(--app-accent-solid)', color: '#fff' }
                : { background: 'var(--app-surface)', color: 'var(--app-text-subtle)' }
            }
          >
            {track.label}
          </button>
        ))}
      </div>

      {/* Session list */}
      <div className="app-nav-content flex-1 overflow-y-auto">
        {!loading && (() => {
          const dayConflicts = savedSessions.filter((s) => s.date === activeDay && conflictSet.has(s.id));
          if (dayConflicts.length === 0) return null;
          return (
            <div
              className="mx-4 mt-3 mb-1 rounded-xl px-3 py-2.5 flex items-center gap-2 border"
              style={{ background: 'rgba(245,158,11,0.1)', borderColor: 'rgba(245,158,11,0.35)' }}
            >
              <AlertTriangle className="w-4 h-4 shrink-0" style={{ color: '#f59e0b' }} />
              <span className="text-xs" style={{ color: '#f59e0b' }}>
                {dayConflicts.length} of your saved sessions overlap on this day
              </span>
            </div>
          );
        })()}
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--app-accent)' }} />
          </div>
        ) : !currentEventId ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 px-8 text-center">
            <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>No event selected</p>
            <button
              className="text-xs underline"
              style={{ color: 'var(--app-accent)' }}
              onClick={() => navigate('/event-selector')}
            >
              Choose an event
            </button>
          </div>
        ) : agendaItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 px-8 text-center">
            <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>
              {days.length === 0 ? 'No sessions added yet' : 'No sessions for this day / track'}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-0">
            {agendaItems.map((session) => {
              const sourceSessionId = session.sourceSessionId ?? session.id;
              const fav = isFavorite(sourceSessionId, 'session');
              const ts = trackStyle(session.type);
              const speaker = session.speakers?.[0]?.profile;
              return (
                <button
                  key={session.id}
                  onClick={() => navigate(`/session/${sourceSessionId}${session.topic ? `?topic=${encodeURIComponent(session.topic)}` : ''}`)}
                  className="flex gap-3 px-4 py-4 border-b text-left active:opacity-70 transition-opacity"
                  style={{ borderColor: 'var(--app-divider)' }}
                >
                  {/* Time column */}
                  <div className="w-16 shrink-0 flex flex-col items-end gap-0.5 pt-0.5">
                    <span className="text-xs" style={{ color: 'var(--app-text-muted)' }}>
                      {formatTime(session.start_time)}
                    </span>
                    <span className="text-[10px]" style={{ color: 'var(--app-text-subtle)' }}>
                      {formatTime(session.end_time)}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {/* Track badge */}
                    <span
                      className="inline-block text-[10px] px-2 py-0.5 rounded-full mb-1.5 capitalize"
                      style={{ background: ts.bg, color: ts.text }}
                    >
                      {sessionTypeLabel(session.type, agendaFilterOverrides)}
                    </span>

                    <p className="text-sm leading-snug" style={{ color: 'var(--app-text)' }}>
                      {session.title}
                    </p>

                    {session.topic && (
                      <p className="text-xs mt-1" style={{ color: 'var(--app-text-muted)' }}>
                        {sessionTypeLabel(session.type, agendaFilterOverrides)} · {session.sourceSessionTitle}
                      </p>
                    )}

                    {speaker && (
                      <p className="text-xs mt-1" style={{ color: 'var(--app-text-muted)' }}>
                        {speaker.name}
                      </p>
                    )}

                    {session.room && (
                      <div className="flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" style={{ color: 'var(--app-text-subtle)' }} />
                        <span className="text-[11px]" style={{ color: 'var(--app-text-subtle)' }}>
                          {session.room}
                        </span>
                      </div>
                    )}

                    {/* Live indicator */}
                    {session.status === 'live' && (
                      <div className="flex items-center gap-1 mt-1.5">
                        <span className="size-1.5 rounded-full bg-[#ef4444]" />
                        <span className="text-[10px] text-[#ef4444]">Live now</span>
                      </div>
                    )}

                    {/* Schedule conflict */}
                    {fav && conflictSet.has(sourceSessionId) && (
                      <div className="flex items-center gap-1 mt-1.5">
                        <AlertTriangle className="w-3 h-3" style={{ color: '#f59e0b' }} />
                        <span className="text-[10px]" style={{ color: '#f59e0b' }}>
                          Overlaps another saved session
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Bookmark */}
                  <button
                    onClick={(e) => toggleSave(sourceSessionId, e)}
                    className="p-1 shrink-0 active:scale-90 transition-transform mt-0.5"
                  >
                    <Bookmark
                      className="w-4 h-4"
                      style={{ color: fav ? 'var(--app-accent)' : 'var(--app-text-subtle)' }}
                      fill={fav ? 'var(--app-accent)' : 'none'}
                    />
                  </button>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
